import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { createPortal } from 'react-dom';
import { getConnections } from '../services/connectionService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Share2 } from 'lucide-react';

const ShareModal = ({ isOpen, onClose, onShare, item, currentUser, type = 'card' }) => {
  const [recipientId, setRecipientId] = useState('');
  const [withEditAccess, setWithEditAccess] = useState(false);
  const [error, setError] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConnections = async () => {
      console.log('Loading connections with currentUser:', currentUser);
      if (!currentUser?.uid) return;
      try {
        const rawConnections = await getConnections(currentUser.uid);
        
        // Filter valid connections first
        const validConnections = rawConnections.filter(conn => 
          conn?.users?.length === 2 && 
          conn.users.every(id => id !== null) &&
          conn.status === 'active'
        );

        // Get user details for each valid connection
        const enrichedConnections = await Promise.all(
          validConnections.map(async (conn) => {
            const otherUserId = conn.users.find(id => id !== currentUser.uid);
            if (!otherUserId) return null;
            
            const userDoc = await getDoc(doc(db, 'users', otherUserId));
            if (!userDoc.exists()) return null;
            
            return {
              id: otherUserId,
              displayName: userDoc.data()?.displayName || userDoc.data()?.email || 'Unknown User',
              email: userDoc.data()?.email
            };
          })
        );
        
        // Filter out null values and set connections
        setConnections(enrichedConnections.filter(conn => conn !== null));
      } catch (err) {
        console.error('Error loading connections:', err);
        setError('Failed to load connections');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      loadConnections();
    }
  }, [isOpen, currentUser]);

  const handleShare = async (e) => {
    e.preventDefault();
    if (!recipientId) {
      setError('Please select a recipient');
      return;
    }
    try {
      await onShare(recipientId, item, withEditAccess);
      onClose();
    } catch (err) {
      console.error('Error sharing:', err);
      setError('Failed to share item');
    }
  };

  return createPortal(
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
          <div className="bg-[#04b53a] p-6">
            <Dialog.Title className="flex items-center gap-2 text-xl font-semibold text-white">
              <Share2 className="w-6 h-6" />
              Share {type}
            </Dialog.Title>
          </div>

          {loading ? (
            <div className="p-6">
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 rounded-full bg-[#04b53a] animate-bounce [animation-delay:-0.3s]"></div>
                <div className="w-4 h-4 rounded-full bg-[#04b53a] animate-bounce [animation-delay:-0.15s]"></div>
                <div className="w-4 h-4 rounded-full bg-[#04b53a] animate-bounce"></div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleShare} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select a connection
                  </label>
                  <select
                    value={recipientId}
                    onChange={(e) => setRecipientId(e.target.value)}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-[#04b53a] focus:ring-[#04b53a]"
                  >
                    <option value="">Choose a connection</option>
                    {connections.map((connection) => (
                      <option key={connection.id} value={connection.id}>
                        {connection.displayName || connection.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="editAccess"
                    checked={withEditAccess}
                    onChange={(e) => setWithEditAccess(e.target.checked)}
                    className="rounded border-gray-300 text-[#04b53a] focus:ring-[#04b53a]"
                  />
                  <label htmlFor="editAccess" className="ml-2 text-sm text-gray-600">
                    Allow editing
                  </label>
                </div>

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                    {error}
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-[#04b53a] rounded-lg hover:bg-[#039431] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#04b53a]"
                >
                  Share
                </button>
              </div>
            </form>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>,
    document.body
  );
};

export default ShareModal;